// src/hooks/useNotifications.js
// ✅ Fetch notifiche con polling ogni 30s
// ✅ markAsRead, markAllRead, deleteNotification
// ✅ unreadCount calcolato

import { useState, useEffect, useCallback, useRef } from 'react';
import axiosConfig from '../api/axiosConfig';
import { useAuth } from '../context/AuthContext';

const POLL_INTERVAL = 30_000; // 30 secondi

export const useNotifications = () => {
    const { isAuthenticated } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const isMounted = useRef(true);
    const intervalRef = useRef(null);

    const fetch = useCallback(async () => {
        if (!isAuthenticated || !isMounted.current) return;
        try {
            const res = await axiosConfig.get('/notifications');
            if (isMounted.current) {
                setNotifications(res.data?.data || []);
            }
        } catch {
            // silenzioso — non mostrare errori per il polling
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated) return;
        setLoading(true);
        fetch().finally(() => { if (isMounted.current) setLoading(false); });

        intervalRef.current = setInterval(fetch, POLL_INTERVAL);
        return () => {
            clearInterval(intervalRef.current);
            isMounted.current = false;
        };
    }, [isAuthenticated, fetch]);

    const markAsRead = async (id) => {
        try {
            await axiosConfig.put(`/notifications/${id}/read`);
            setNotifications(prev =>
                prev.map(n => n.id === id ? { ...n, read: true } : n)
            );
        } catch { /* silenzioso */ }
    };

    const markAllRead = async () => {
        try {
            await axiosConfig.put('/notifications/read-all');
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        } catch { /* silenzioso */ }
    };

    const deleteNotification = async (id) => {
        try {
            await axiosConfig.delete(`/notifications/${id}`);
            setNotifications(prev => prev.filter(n => n.id !== id));
        } catch { /* silenzioso */ }
    };

    const unreadCount = notifications.filter(n => !n.read).length;

    return {
        notifications,
        loading,
        unreadCount,
        markAsRead,
        markAllRead,
        deleteNotification,
        refresh: fetch,
    };
};